import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  DocumentReference,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "@/firebase";

const TRIGGER_OPTIONS = [
  { value: "onCreate", label: "onCreate" },
  { value: "onUpdate", label: "onUpdate" },
] as const;

const DSTATUS_OPTIONS = [
  { value: "", label: "指定しない" },
  { value: "ready", label: "ready" },
  { value: "done", label: "done" },
  { value: "stop", label: "stop" },
] as const;

type TriggerType = (typeof TRIGGER_OPTIONS)[number]["value"];

type InventoryRuleConditions = {
  shikakuId?: DocumentReference;
  course?: string;
  accessories?: string;
  Dstatus?: string;
};

type InventoryRuleMap = {
  id: string;
  trigger: TriggerType;
  conditions: InventoryRuleConditions;
  enabled: boolean;
  items: {
    inventoryRef: DocumentReference;
    decrement: number;
  }[];
};

type ShikakuOption = {
  id: string;
  name: string;
};

type InventoryOption = {
  id: string;
  name: string;
};

const emptyFormState = {
  id: "",
  trigger: "onCreate" as TriggerType,
  shikakuId: "",
  course: "",
  accessories: "",
  Dstatus: "",
  items: [{ inventoryId: "", decrement: 1 }],
  enabled: true,
};

type FormState = typeof emptyFormState;

const formatConditionLabel = (
  label: string,
  value: string,
  variant: "primary" | "neutral" = "primary",
) => {
  const base =
    variant === "primary"
      ? "bg-emerald-50 text-emerald-700"
      : "bg-gray-100 text-gray-700";

  return (
    <span
      key={`${label}-${value}`}
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${base}`}
    >
      {label}: {value}
    </span>
  );
};

export default function InventoryRuleSetsPage() {
  const [ruleMaps, setRuleMaps] = useState<InventoryRuleMap[]>([]);
  const [shikakuOptions, setShikakuOptions] = useState<ShikakuOption[]>([]);
  const [inventoryOptions, setInventoryOptions] = useState<InventoryOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formState, setFormState] = useState<FormState>(emptyFormState);
  const [saving, setSaving] = useState(false);
  const [toggleLoading, setToggleLoading] = useState<string | null>(null);

  useEffect(() => {
    const ruleMapQuery = query(
      collection(db, "InventoryRuleMap"),
      orderBy("updatedAt", "desc"),
    );

    const unsubscribeRuleMaps = onSnapshot(
      ruleMapQuery,
      (snapshot) => {
        const fetched = snapshot.docs.map((ruleDoc) => {
          const data = ruleDoc.data();
          const conditions = (data.conditions ?? {}) as InventoryRuleConditions;
          const items = Array.isArray(data.items) ? data.items : [];
          return {
            id: ruleDoc.id,
            trigger: (data.trigger ?? "onCreate") as TriggerType,
            conditions,
            enabled: Boolean(data.enabled),
            items: items.map((item: any) => ({
              inventoryRef: item.inventoryRef,
              decrement: Number(item.decrement ?? 0),
            })),
          } satisfies InventoryRuleMap;
        });

        setRuleMaps(fetched);
        setLoading(false);
      },
      (snapshotError) => {
        console.error(snapshotError);
        setError("ルールの取得に失敗しました。");
        setLoading(false);
      },
    );

    const shikakuQuery = query(collection(db, "Shikaku"), orderBy("ShikakuName"));
    const unsubscribeShikaku = onSnapshot(
      shikakuQuery,
      (snapshot) => {
        const fetched = snapshot.docs.map((shikakuDoc) => ({
          id: shikakuDoc.id,
          name: shikakuDoc.data().ShikakuName ?? "",
        }));
        setShikakuOptions(fetched);
      },
      (snapshotError) => {
        console.error(snapshotError);
        setError("資格データの取得に失敗しました。");
      },
    );

    const inventoryQuery = query(collection(db, "Inventories"), orderBy("sortNo", "asc"));
    const unsubscribeInventories = onSnapshot(
      inventoryQuery,
      (snapshot) => {
        const fetched = snapshot.docs.map((inventoryDoc) => ({
          id: inventoryDoc.id,
          name: inventoryDoc.data().name ?? "",
        }));
        setInventoryOptions(fetched);
      },
      (snapshotError) => {
        console.error(snapshotError);
        setError("在庫データの取得に失敗しました。");
      },
    );

    return () => {
      unsubscribeRuleMaps();
      unsubscribeShikaku();
      unsubscribeInventories();
    };
  }, []);

  const shikakuMap = useMemo(() => {
    return new Map(shikakuOptions.map((option) => [option.id, option.name]));
  }, [shikakuOptions]);

  const inventoryMap = useMemo(() => {
    return new Map(inventoryOptions.map((option) => [option.id, option.name]));
  }, [inventoryOptions]);

  const conditionTags = (conditions: InventoryRuleConditions) => {
    const tags = [] as JSX.Element[];
    if (conditions.shikakuId) {
      const label = shikakuMap.get(conditions.shikakuId.id) ?? conditions.shikakuId.id;
      tags.push(formatConditionLabel("資格", label));
    }
    if (conditions.course) {
      tags.push(formatConditionLabel("course", conditions.course));
    }
    if (conditions.accessories) {
      tags.push(formatConditionLabel("accessories", conditions.accessories));
    }
    if (conditions.Dstatus) {
      tags.push(formatConditionLabel("Dstatus", conditions.Dstatus));
    }
    if (tags.length === 0) {
      tags.push(formatConditionLabel("条件", "指定なし", "neutral"));
    }
    return tags;
  };

  const handleOpenCreate = () => {
    setFormState(emptyFormState);
    setIsFormOpen(true);
  };

  const handleOpenEdit = (ruleMap: InventoryRuleMap) => {
    setFormState({
      id: ruleMap.id,
      trigger: ruleMap.trigger,
      shikakuId: ruleMap.conditions.shikakuId?.id ?? "",
      course: ruleMap.conditions.course ?? "",
      accessories: ruleMap.conditions.accessories ?? "",
      Dstatus: ruleMap.conditions.Dstatus ?? "",
      items: ruleMap.items.length
        ? ruleMap.items.map((item) => ({
            inventoryId: item.inventoryRef?.id ?? "",
            decrement: item.decrement,
          }))
        : [{ inventoryId: "", decrement: 1 }],
      enabled: ruleMap.enabled,
    });
    setIsFormOpen(true);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving) return;

    setSaving(true);
    setError(null);

    try {
      const conditions: InventoryRuleConditions = {};
      const course = formState.course.trim();
      const accessories = formState.accessories.trim();
      const preparedItems = formState.items
        .map((item) => ({
          inventoryId: item.inventoryId,
          decrement: Number(item.decrement),
        }))
        .filter((item) => item.inventoryId);

      if (formState.shikakuId) {
        conditions.shikakuId = doc(db, "Shikaku", formState.shikakuId);
      }
      if (course) {
        conditions.course = course;
      }
      if (accessories) {
        conditions.accessories = accessories;
      }
      if (formState.Dstatus) {
        conditions.Dstatus = formState.Dstatus;
      }

      if (preparedItems.length === 0) {
        setError("在庫を1件以上選択してください。");
        return;
      }
      if (preparedItems.some((item) => !Number.isFinite(item.decrement) || item.decrement <= 0)) {
        setError("decrement は 1 以上で入力してください。");
        return;
      }

      const payload = {
        trigger: formState.trigger,
        conditions,
        enabled: formState.enabled,
        items: preparedItems.map((item) => ({
          inventoryRef: doc(db, "Inventories", item.inventoryId),
          decrement: item.decrement,
        })),
        updatedAt: serverTimestamp(),
      };

      if (formState.id) {
        await updateDoc(doc(db, "InventoryRuleMap", formState.id), payload);
      } else {
        await addDoc(collection(db, "InventoryRuleMap"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }

      setIsFormOpen(false);
      setFormState(emptyFormState);
    } catch (submitError) {
      console.error(submitError);
      setError("保存に失敗しました。");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ruleMapId: string) => {
    if (!window.confirm("このルールを削除しますか？")) return;

    try {
      await deleteDoc(doc(db, "InventoryRuleMap", ruleMapId));
    } catch (deleteError) {
      console.error(deleteError);
      setError("削除に失敗しました。");
    }
  };

  const handleToggleEnabled = async (ruleMap: InventoryRuleMap) => {
    try {
      setToggleLoading(ruleMap.id);
      await updateDoc(doc(db, "InventoryRuleMap", ruleMap.id), {
        enabled: !ruleMap.enabled,
        updatedAt: serverTimestamp(),
      });
    } catch (toggleError) {
      console.error(toggleError);
      setError("ステータスの更新に失敗しました。");
    } finally {
      setToggleLoading(null);
    }
  };

  return (
    <div className="p-6 md:p-8 space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">在庫ルール条件</h2>
          <p className="mt-2 text-sm text-gray-600">
            Order の条件とトリガーに応じて発動する在庫ルールを管理します。
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={handleOpenCreate}
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-emerald-600"
          >
            <i className="ri-add-line text-lg" />
            ルールを追加
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
          {error}
        </div>
      )}

      <section className="rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="border-b border-gray-100 px-6 py-4">
          <h3 className="text-sm font-semibold text-gray-800">ルール一覧</h3>
        </div>
        <div className="divide-y divide-gray-100">
          {loading ? (
            <div className="px-6 py-6 text-sm text-gray-500">読み込み中...</div>
          ) : ruleMaps.length === 0 ? (
            <div className="px-6 py-6 text-sm text-gray-500">ルールが登録されていません。</div>
          ) : (
            ruleMaps.map((ruleMap) => (
              <div key={ruleMap.id} className="flex flex-col gap-4 px-6 py-5 md:flex-row md:items-center">
                <div className="flex-1 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-600">
                      {ruleMap.trigger}
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {conditionTags(ruleMap.conditions)}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                    {ruleMap.items.length === 0 ? (
                      <span>在庫: 未設定</span>
                    ) : (
                      ruleMap.items.map((item, index) => (
                        <span key={`${ruleMap.id}-item-${index}`}>
                          在庫: {inventoryMap.get(item.inventoryRef.id) ?? item.inventoryRef.id} / 減算:{" "}
                          {item.decrement}
                        </span>
                      ))
                    )}
                  </div>
                  <p className="text-xs text-gray-500">ID: {ruleMap.id}</p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => handleToggleEnabled(ruleMap)}
                    disabled={toggleLoading === ruleMap.id}
                    className={`inline-flex h-9 w-16 items-center rounded-full px-1 transition-colors ${
                      ruleMap.enabled ? "bg-emerald-500" : "bg-gray-300"
                    }`}
                    aria-pressed={ruleMap.enabled}
                  >
                    <span
                      className={`inline-block h-7 w-7 rounded-full bg-white shadow transition-transform ${
                        ruleMap.enabled ? "translate-x-7" : "translate-x-0"
                      }`}
                    />
                  </button>
                  <span className="text-xs font-semibold text-gray-600">
                    {ruleMap.enabled ? "有効" : "無効"}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 md:justify-end">
                  <button
                    type="button"
                    onClick={() => handleOpenEdit(ruleMap)}
                    className="rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                  >
                    編集
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(ruleMap.id)}
                    className="rounded-lg border border-red-100 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50"
                  >
                    削除
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {isFormOpen && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-xl rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
              <div>
                <p className="text-xs font-semibold text-emerald-600">RULE SET</p>
                <h3 className="text-lg font-semibold text-gray-900">
                  {formState.id ? "ルールを編集" : "ルールを追加"}
                </h3>
              </div>
              <button
                type="button"
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setIsFormOpen(false)}
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            <form className="space-y-4 p-6" onSubmit={handleSubmit}>
              <div className="space-y-1">
                <label className="text-sm font-semibold text-gray-800">trigger</label>
                <select
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                  value={formState.trigger}
                  onChange={(event) =>
                    setFormState((prev) => ({
                      ...prev,
                      trigger: event.target.value as TriggerType,
                    }))
                  }
                >
                  {TRIGGER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-semibold text-gray-800">shikakuId</label>
                <select
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                  value={formState.shikakuId}
                  onChange={(event) =>
                    setFormState((prev) => ({
                      ...prev,
                      shikakuId: event.target.value,
                    }))
                  }
                >
                  <option value="">指定しない</option>
                  {shikakuOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1">
                  <label className="text-sm font-semibold text-gray-800">course</label>
                  <input
                    type="text"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                    value={formState.course}
                    onChange={(event) =>
                      setFormState((prev) => ({
                        ...prev,
                        course: event.target.value,
                      }))
                    }
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-semibold text-gray-800">accessories</label>
                  <input
                    type="text"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                    value={formState.accessories}
                    onChange={(event) =>
                      setFormState((prev) => ({
                        ...prev,
                        accessories: event.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-semibold text-gray-800">Dstatus</label>
                <select
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                  value={formState.Dstatus}
                  onChange={(event) =>
                    setFormState((prev) => ({
                      ...prev,
                      Dstatus: event.target.value,
                    }))
                  }
                >
                  {DSTATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold text-gray-800">inventory</label>
                  <button
                    type="button"
                    className="text-xs font-semibold text-emerald-600 hover:text-emerald-500"
                    onClick={() =>
                      setFormState((prev) => ({
                        ...prev,
                        items: [...prev.items, { inventoryId: "", decrement: 1 }],
                      }))
                    }
                  >
                    + 在庫を追加
                  </button>
                </div>
                <div className="space-y-3">
                  {formState.items.map((item, index) => (
                    <div key={`inventory-item-${index}`} className="grid gap-3 md:grid-cols-[1fr_140px_auto]">
                      <select
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                        value={item.inventoryId}
                        onChange={(event) =>
                          setFormState((prev) => ({
                            ...prev,
                            items: prev.items.map((prevItem, itemIndex) =>
                              itemIndex === index
                                ? { ...prevItem, inventoryId: event.target.value }
                                : prevItem,
                            ),
                          }))
                        }
                      >
                        <option value="">在庫を選択</option>
                        {inventoryOptions.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.name}
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        min={1}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                        value={item.decrement}
                        onChange={(event) =>
                          setFormState((prev) => ({
                            ...prev,
                            items: prev.items.map((prevItem, itemIndex) =>
                              itemIndex === index
                                ? { ...prevItem, decrement: Number(event.target.value) }
                                : prevItem,
                            ),
                          }))
                        }
                      />
                      <button
                        type="button"
                        className="text-xs font-semibold text-gray-500 hover:text-red-500 disabled:opacity-40"
                        onClick={() =>
                          setFormState((prev) => ({
                            ...prev,
                            items: prev.items.filter((_, itemIndex) => itemIndex !== index),
                          }))
                        }
                        disabled={formState.items.length === 1}
                      >
                        削除
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between rounded-xl border border-gray-100 px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-gray-800">有効/無効</p>
                  <p className="text-xs text-gray-500">ON のルールのみが評価されます。</p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setFormState((prev) => ({
                      ...prev,
                      enabled: !prev.enabled,
                    }))
                  }
                  className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                    formState.enabled ? "bg-emerald-500" : "bg-gray-300"
                  }`}
                  aria-pressed={formState.enabled}
                >
                  <span
                    className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${
                      formState.enabled ? "translate-x-7" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  onClick={() => setIsFormOpen(false)}
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={saving}
                >
                  {saving && <span className="h-2 w-2 rounded-full bg-white animate-pulse" />}
                  保存する
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
